// AI provider error handler.
//
// Detects quota / billing exhaustion responses (OpenAI insufficient_quota,
// Gemini quota exceeded, DeepSeek payment required, raw 429) coming from the
// backend AI endpoints and surfaces a friendlier toast with a one-click
// "Switch to Emergent (Universal Key)" action.
//
// Use from any AI-calling page:
//   import { handleAIError } from '../lib/aiErrorHandler';
//   ...
//   try { await api.post('/ai/...') }
//   catch (err) { handleAIError(err, 'Failed to check interactions'); }
import { toast } from 'sonner';
import api from './api';

const QUOTA_NEEDLES = [
  'insufficient_quota',
  'exceeded your current quota',
  'quota exceeded',
  'billing',
  'payment required',
  'ai_quota_exhausted',
];

export function isAIQuotaError(err) {
  if (!err) return false;
  const status = err?.response?.status;
  const data = err?.response?.data;
  const code = data?.code || data?.error?.code;
  if (code === 'ai_quota_exhausted') return true;
  if (status === 429) return true;
  const haystack = (
    JSON.stringify(data || '') + ' ' + (err?.message || '')
  ).toLowerCase();
  return QUOTA_NEEDLES.some((n) => haystack.includes(n));
}

export async function switchToEmergentProvider() {
  // Patch only ai_config.provider — keep all other provider configs intact.
  const { data: current } = await api.get('/settings');
  const ai_config = { ...(current.ai_config || {}), provider: 'emergent' };
  await api.put('/settings', { ai_config });
}

/** Show the friendly quota toast with a "Switch to Emergent" action button. */
export function showAIQuotaToast() {
  toast.error('AI quota exhausted on your provider', {
    duration: 12000,
    description:
      'Switch to Emergent (Universal Key) for instant fallback, or top up your provider balance at the provider dashboard.',
    action: {
      label: 'Switch to Emergent',
      onClick: async () => {
        try {
          await switchToEmergentProvider();
          toast.success('AI provider switched to Emergent. Reloading...', { duration: 3500 });
          setTimeout(() => window.location.reload(), 1200);
        } catch (e) {
          toast.error('Failed to switch provider', {
            description:
              e?.response?.data?.detail ||
              'Open Settings → AI manually and select "Emergent (Universal Key)".',
          });
        }
      },
    },
  });
}

/**
 * Generic AI-call error handler. Returns true when it surfaced the friendly
 * quota toast (callers can choose to skip their own toast in that case).
 */
export function handleAIError(err, fallbackMessage = 'AI request failed') {
  if (isAIQuotaError(err)) {
    showAIQuotaToast();
    return true;
  }
  const detail = err?.response?.data?.detail || err?.message || fallbackMessage;
  toast.error(typeof detail === 'string' ? detail : fallbackMessage);
  return false;
}
